import { LEGAL_DOCUMENT_KEYS, LEGAL_DOCUMENT_TITLES } from "@ai-crm/shared";
import type { AdminLegalDocumentCatalogItemDTO, AdminLegalDocumentVersionDTO, LegalDocumentKey } from "@ai-crm/shared";
import { useState } from "react";
import { DocumentVersionForm } from "../components/document-version-form";
import { PageHeader } from "../components/layout";
import { Pagination } from "../components/pagination";
import { adminApi } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { formatMoscowDateTime } from "../lib/format";
import { usePaymentDocuments } from "../lib/payment-documents";
import { paymentButton, paymentPrimary, usePaymentScope } from "../lib/payment-ui";
import { useLegalDocumentCatalog, useLegalDocumentHistory } from "../lib/use-legal-documents";

export function DocumentsPage() {
  const { admin, token } = useAdminAuth();
  return <DocumentsWorkspace key={`${admin?.id}:${token}`} />;
}

function DocumentsWorkspace() {
  const catalog = useLegalDocumentCatalog();
  return (
    <div>
      <PageHeader title="Документы" description="Юридические документы: загрузка, проверка PDF и немедленная публикация редакции." />
      <div className="max-w-4xl space-y-4">
        {catalog.isPending && <p role="status">Загружаем документы…</p>}
        {catalog.isError && <p role="alert">Не удалось загрузить документы. <button className={paymentButton} onClick={() => void catalog.refetch()}>Повторить</button></p>}
        {LEGAL_DOCUMENT_KEYS.map((key) => (
          <DocumentCard key={key} documentKey={key} item={catalog.data?.items.find((item) => item.key === key)}
            catalogReady={Boolean(catalog.data) && !catalog.isError && !catalog.isFetching} />
        ))}
      </div>
    </div>
  );
}

function documentTime(value: string | null) {
  return value ? `${formatMoscowDateTime(value)} МСК` : "Нет данных";
}

interface DocumentCardProps {
  documentKey: LegalDocumentKey;
  item: AdminLegalDocumentCatalogItemDTO | undefined;
  catalogReady: boolean;
}
function DocumentCard({ documentKey, item, catalogReady }: DocumentCardProps) {
  const scope = usePaymentScope();
  const docs = usePaymentDocuments();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const history = useLegalDocumentHistory(documentKey, cursorStack.at(-1));
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<AdminLegalDocumentVersionDTO | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const title = LEGAL_DOCUMENT_TITLES[documentKey];
  const active = item?.activeVersion;
  const knownPublished = Boolean(selected && (
    selected.publishedAt || active?.id === selected.id ||
    history.data?.items.some((version) => version.id === selected.id && version.publishedAt)
  ));
  function pdf(version: AdminLegalDocumentVersionDTO, open: boolean) {
    if (!scope.token) return;
    return docs.fetchDocument(
      () => adminApi.legalDocumentVersionPdf(scope.token!, documentKey, version.id),
      `${documentKey}-${version.editionDate}.pdf`, open,
    );
  }
  return (
    <section className="panel space-y-4 p-4" aria-label={title}>
      <h2 className="text-lg font-semibold">{title}</h2>
      {active ? (
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div><dt className="text-muted-foreground">Текущая редакция</dt><dd>{active.editionDate}</dd></div>
          <div><dt className="text-muted-foreground">Опубликована</dt><dd>{documentTime(active.publishedAt)}</dd></div>
        </dl>
      ) : item ? (
        <p className="text-sm">{documentKey === "offer" ? "Оферта ещё не опубликована. Без действующей оферты нельзя сформировать счёт." : "Документ ещё не опубликован."}</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button className={paymentButton} disabled={!active || docs.busy} onClick={() => active && void pdf(active, true)}>Открыть</button>
        <button className={paymentButton} disabled={!active || docs.busy} onClick={() => active && void pdf(active, false)}>Скачать</button>
        <button className={paymentPrimary} disabled={!item || formOpen} onClick={() => { setSelected(null); setFormOpen(true); setMessage(null); }}>Загрузить редакцию</button>
      </div>
      {formOpen && item && (
        <DocumentVersionForm documentKey={documentKey} title={title} revision={item.revision}
          catalogReady={catalogReady} activeEdition={active?.editionDate ?? null}
          initialDraft={selected} knownPublished={knownPublished}
          onUploaded={(version) => { setSelected(version); setCursorStack([]); }}
          onPublished={() => { setFormOpen(false); setSelected(null); setCursorStack([]); setMessage("Редакция опубликована."); }}
          onClose={() => { setFormOpen(false); setSelected(null); }} />
      )}
      {docs.error && <p role="alert" className="text-sm text-danger">{docs.error}</p>}
      {message && <p role="status">{message}</p>}
      <section className="space-y-3" aria-label={`История: ${title}`}>
        <h3 className="font-semibold">История редакций</h3>
        {history.isPending && <p role="status" className="text-sm">Загружаем историю…</p>}
        {history.isError && <p role="alert" className="text-sm">Не удалось загрузить историю. <button className={paymentButton} onClick={() => void history.refetch()}>Повторить</button></p>}
        {history.data?.items.length === 0 && <p className="text-sm text-muted-foreground">Редакций пока нет.</p>}
        {history.data?.items.map((version) => (
          <article key={version.id} className="space-y-3 rounded-md border p-3 text-sm">
            <p className="font-medium">{version.editionDate} · {version.isCurrent ? "Актуальная" : version.publishedAt ? "Архивная" : "Черновик"}</p>
            <dl className="grid gap-2 sm:grid-cols-2">
              <div><dt className="text-muted-foreground">Загружена</dt><dd>{documentTime(version.uploadedAt)}</dd></div>
              <div><dt className="text-muted-foreground">Автор загрузки</dt><dd className="break-all">{version.uploadedBy ?? "Нет данных"}</dd></div>
              <div><dt className="text-muted-foreground">Опубликована</dt><dd>{documentTime(version.publishedAt)}</dd></div>
              <div><dt className="text-muted-foreground">Автор публикации</dt><dd className="break-all">{version.publishedBy ?? "Нет данных"}</dd></div>
            </dl>
            <div className="flex flex-wrap gap-2">
              <button className={paymentButton} disabled={docs.busy} onClick={() => void pdf(version, true)}>Открыть PDF</button>
              <button className={paymentButton} disabled={docs.busy} onClick={() => void pdf(version, false)}>Скачать PDF</button>
              {!version.publishedAt && <button className={paymentButton} disabled={formOpen || !catalogReady} onClick={() => { setSelected(version); setFormOpen(true); setMessage(null); }}>Проверить и опубликовать</button>}
            </div>
          </article>
        ))}
        <Pagination canGoBack={cursorStack.length > 0}
          canGoForward={Boolean(history.data?.nextCursor && !cursorStack.includes(history.data.nextCursor))}
          isLoading={history.isFetching} onBack={() => setCursorStack((stack) => stack.slice(0, -1))}
          onForward={() => { const next = history.data?.nextCursor; if (next && !cursorStack.includes(next)) setCursorStack((stack) => [...stack, next]); }} />
      </section>
    </section>
  );
}
